<template>
  <div class="ssc" :class="'ssc--' + state.kind" :data-test="tid('switch-card')">
    <div class="ssc-line">
      <span class="ssc-icon"><i class="fa" :class="icon" aria-hidden="true"></i></span>
      <span :id="labelId" class="ssc-title" :data-test="tid('switch-title')">{{ title }}</span>
      <button
        type="button"
        class="ssc-switch"
        :class="{ 'ssc-switch--on': state.checked }"
        role="switch"
        :aria-checked="state.checked ? 'true' : 'false'"
        :aria-labelledby="labelId"
        :disabled="!state.canToggle || busy"
        :data-test="tid('switch')"
        @click="onClick"
      ><span class="ssc-knob"></span></button>
    </div>
    <div class="ssc-msg" :class="{ 'ssc-msg--blocked': state.kind === 'blocked' }" :data-test="tid('switch-message')">
      <i v-if="state.kind === 'blocked'" class="fa fa-lock" aria-hidden="true" :data-test="tid('switch-lock')"></i>
      <i v-else-if="state.kind === 'paused'" class="fa fa-pause-circle" aria-hidden="true" :data-test="tid('switch-paused')"></i>
      <span>{{ state.message }}</span>
    </div>
    <div v-if="error" class="ssc-error" role="alert" :data-test="tid('switch-error')">
      <i class="fa fa-exclamation-circle" aria-hidden="true"></i>{{ error }}
    </div>

    <!-- The confirm dialog (OFF -> ON only). Controlled by the parent through `confirmOpen`. -->
    <div v-if="confirmOpen" class="ssc-scrim" :data-test="tid('confirm-scrim')">
      <div
        class="ssc-dialog"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="dialogTitleId"
        :data-test="tid('confirm')"
        @keydown.esc.stop="$emit('cancel')"
      >
        <div class="ssc-dbody">
          <div :id="dialogTitleId" class="ssc-dtitle" :data-test="tid('confirm-title')">{{ confirmTitle }}</div>
          <p v-if="confirmBody" class="ssc-dlead" :data-test="tid('confirm-body')">{{ confirmBody }}</p>
          <slot name="confirm-details"></slot>
          <div v-if="confirmFine" class="ssc-dfine" :data-test="tid('confirm-fine')">{{ confirmFine }}</div>
        </div>
        <div class="ssc-dfoot">
          <button ref="cancel" type="button" class="ssc-btn" :data-test="tid('confirm-cancel')" @click="$emit('cancel')">Cancel</button>
          <button type="button" class="ssc-btn ssc-btn--primary" :disabled="busy" :data-test="tid('confirm-ok')" @click="$emit('confirm')">{{ confirmOk }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The send-switch card every sending tab of Forms Settings opens with (plan docs/plans/2026-10-20-forms-settings-plan.md D10, D15a, §2.1),
 * generalised from AutoSendSwitch (the shipped post-op header switch) + AutoSendConfirm (its confirm dialog). The shipped two-tab editor keeps
 * its own two components untouched.
 *
 *   Off      "Off — …"                      the switch is free
 *   On       "On since <server liveSince>…"  the switch can always be turned OFF
 *   Blocked  a lock + the FIRST reason       the switch is locked (it can still be turned OFF while it is ON)
 *   Paused   a pause icon, amber             ON, but the sender yields for now (an active workflow sends instead); can be turned OFF
 *
 * PRESENTATIONAL AND CONTROLLED: the parent decides the state (`state` = { kind:'off'|'on'|'paused'|'blocked', checked, canToggle, message },
 * e.g. utils/postOp/readiness.js switchState) and renders the SERVER's liveSince. Clicking the switch NEVER changes what the card shows:
 *
 *   ON  -> OFF   emits `turn-off` (always allowed)
 *   OFF -> ON    emits `request-on`; the parent opens the confirm by passing `confirmOpen` (this card draws the dialog from
 *                `confirmTitle` / `confirmBody` / `confirmFine` / the `confirm-details` slot), or shows its own dialog (the post-op tab
 *                keeps AutoSendConfirm, its rich counts, untouched)
 *   confirm      OK emits `confirm` (the parent sends the ONE request that carries `enabled`), Cancel / Escape emit `cancel`
 *
 * So there is no way to flip the switch ON without the confirm: the only event that means "turn it on" is `confirm`, and only the dialog emits it.
 *
 * `testId` prefixes every data-test (default 'pfe' = the names the shipped post-op switch / confirm already use; the new tabs pass their own).
 * Escape inside the dialog is stopped here so the editor underneath does not also close.
 */
let seq = 0;

export default {
  name: 'PfeSendSwitchCard',
  props: {
    title: { type: String, required: true },
    icon: { type: String, default: 'fa-paper-plane' },
    state: { type: Object, required: true },
    busy: { type: Boolean, default: false },
    /** The server's own reason when a switch-on was refused (a 400 ENABLE_BLOCKED) or failed. */
    error: { type: String, default: '' },
    testId: { type: String, default: 'pfe' },
    confirmOpen: { type: Boolean, default: false },
    confirmTitle: { type: String, default: '' },
    confirmBody: { type: String, default: '' },
    confirmFine: { type: String, default: '' },
    confirmOk: { type: String, default: 'Turn on' },
  },
  data() {
    seq += 1;
    return { uid: seq };
  },
  computed: {
    labelId() { return `ssc-${this.uid}-label`; },
    dialogTitleId() { return `ssc-${this.uid}-dialog-title`; },
  },
  watch: {
    confirmOpen(open) {
      if (open) this.focusCancel();
    },
  },
  mounted() {
    if (this.confirmOpen) this.focusCancel();
  },
  methods: {
    // The dialog opens on Cancel: Enter never turns a send on by accident.
    focusCancel() {
      this.$nextTick(() => { if (this.$refs.cancel) this.$refs.cancel.focus(); });
    },
    tid(name) { return `${this.testId}-${name}`; },
    onClick() {
      if (this.busy) return;
      if (this.state.checked) { this.$emit('turn-off'); return; }
      if (!this.state.canToggle) return;
      this.$emit('request-on');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.ssc {
  box-sizing: border-box;
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1.5px solid $pp-border;
  background: $pfe-neutral-25;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &--on { border-color: $pp-brand; background: $pp-brand-subtle; }
}
.ssc-line { display: flex; align-items: center; gap: 10px; }
.ssc-icon { display: flex; color: $pfe-neutral-400; .ssc--on & { color: $pp-brand; } }
.ssc-title { flex: 1; font-size: 15px; font-weight: 800; color: $pp-text; }

.ssc-switch {
  position: relative;
  flex: none;
  width: 44px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: $pp-border-strong;
  cursor: pointer;
  transition: background 0.15s ease;

  &--on { background: $pp-brand; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; }
}
.ssc-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(12, 15, 14, 0.25);
  transition: transform 0.15s ease;

  .ssc-switch--on & { transform: translateX(20px); }
}

.ssc-msg {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding-left: 26px;
  font-size: 13px;
  line-height: 1.45;
  color: $pfe-neutral-600;

  .ssc--on & { color: $pp-brand-deep; }
  i { margin-top: 3px; color: $pfe-neutral-500; }
  .ssc--paused & { color: $pfe-amber-ink; i { color: $pfe-amber-ink; } }
}
.ssc-error {
  padding-left: 26px;
  font-size: 13px;
  font-weight: 600;
  color: $pfe-red-ink;

  i { margin-right: 6px; }
}

// The dialog covers the whole editor (fixed inside the editor's own stacking context, below its toast).
.ssc-scrim {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(12, 15, 14, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.ssc-dialog {
  width: 520px;
  max-width: 100%;
  max-height: 100%;
  overflow-y: auto;
  background: $pp-surface;
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(12, 15, 14, 0.28);
}
.ssc-dbody { padding: 24px 26px 6px; display: flex; flex-direction: column; gap: 14px; }
.ssc-dtitle { font-family: $pp-font-display; font-weight: 800; font-size: 22px; letter-spacing: -0.02em; }
.ssc-dlead { margin: 0; font-size: 15px; line-height: 1.5; }
.ssc-dfine { font-size: 13px; color: $pfe-neutral-500; }
.ssc-dfoot { display: flex; justify-content: flex-end; gap: 8px; padding: 18px 26px 22px; }
.ssc-btn {
  @include pfe-button(secondary);
  height: 40px;

  &--primary { @include pfe-button(primary); height: 40px; }
}
</style>
